import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Pencil, Trash2, X } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Section } from '@/shared/ui/layout/Section';
import { FulfillmentChip } from '@/shared/ui/chips/FulfillmentChip';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { Button } from '@/shared/ui/buttons/Button';
import { extractErrorMessage } from '@/shared/api/errors';
import { useParcelDetail } from '@/entities/parcels';
import { useParcelProducts } from '@/entities/parcels-products';
import { useCharges } from '@/entities/charges';
import { usePerm } from '@/shared/hooks/usePerm';
import {
  FeaturesParcelsApproveDialog,
  FeaturesParcelsDeleteDialog,
  FeaturesParcelsRejectDialog,
  FeaturesParcelsWorkflowStatusChanger,
} from '@/features/parcels';
import { FeaturesChargesTable } from '@/features/charges';
import { formatUsd } from '@/shared/utils/currency';
import { formatDateTime } from '@/shared/utils/date';
import type { ApiParcelsGetItem } from '@/shared/types/api/parcels';

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
    <span className="text-ink-muted">{label}</span>
    <span className="text-right font-medium text-ink">{value || '—'}</span>
  </div>
);

export const WidgetsParcelDetailMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const parcelQuery = useParcelDetail(id);
  const productsQuery = useParcelProducts(id);
  const chargesQuery = useCharges({ parcel: id });
  const [approveTarget, setApproveTarget] = useState<ApiParcelsGetItem | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ApiParcelsGetItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ApiParcelsGetItem | null>(null);

  const canApprove = usePerm('parcels.detail.approve');
  const canReject = usePerm('parcels.detail.reject');
  const canEdit = usePerm('parcels.edit');
  const canDelete = usePerm('parcels.delete');

  if (parcelQuery.isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (parcelQuery.error || !parcelQuery.data) {
    return (
      <div className="p-6">
        <ErrorState
          message={extractErrorMessage(parcelQuery.error, t('parcels.detail.notFound'))}
          onRetry={() => void parcelQuery.refetch()}
        />
      </div>
    );
  }

  const parcel = parcelQuery.data;
  const products = productsQuery.data ?? parcel.products ?? [];
  const total = products.reduce((s, p) => s + p.quantity * p.price_per_item, 0);
  const isPending = parcel.status === 'PENDING';
  const hasAnyAction = (isPending && (canApprove || canReject)) || canEdit || canDelete;

  return (
    <>
      <PageHeader
        title={parcel.name}
        subtitle={t('parcels.detail.trek', { trekId: parcel.trek_id })}
        showBack
        backTo="/parcels"
        right={
          hasAnyAction ? (
            <>
              {isPending && canApprove && (
                <Button
                  variant="success"
                  onClick={() => setApproveTarget(parcel)}
                  leftIcon={<Check className="h-4 w-4" />}
                >
                  {t('parcels.detail.approve')}
                </Button>
              )}
              {isPending && canReject && (
                <Button
                  variant="danger"
                  onClick={() => setRejectTarget(parcel)}
                  leftIcon={<X className="h-4 w-4" />}
                >
                  {t('parcels.detail.reject')}
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/parcels/${parcel.id}/edit`)}
                  leftIcon={<Pencil className="h-4 w-4" />}
                >
                  {t('parcels.detail.edit')}
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="danger"
                  onClick={() => setDeleteTarget(parcel)}
                  leftIcon={<Trash2 className="h-4 w-4" />}
                >
                  {t('parcels.detail.delete')}
                </Button>
              )}
            </>
          ) : null
        }
      />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <Section>
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {parcel.fulfillment_status && (
                <FulfillmentChip status={parcel.fulfillment_status} />
              )}
            </div>
            <span className="text-xs text-ink-muted">{formatDateTime(parcel.created_at)}</span>
          </div>
          {parcel.status === 'REJECTED' && parcel.reject_reason && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <strong className="font-semibold">{t('parcels.detail.rejectReasonLabel')}</strong>{' '}
              <span>{parcel.reject_reason}</span>
            </div>
          )}
        </Section>

        {canEdit && (
          <FeaturesParcelsWorkflowStatusChanger
            parcelId={parcel.id}
            currentStatus={parcel.fulfillment_status}
          />
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Section title={t('parcels.detail.sections.address')}>
            <Row label={t('parcels.detail.address.name')} value={parcel.address_name} />
            <Row label={t('parcels.detail.address.region')} value={t(`regions.${parcel.region}`)} />
            <Row label={t('parcels.detail.address.city')} value={parcel.city} />
            <Row
              label={t('parcels.detail.address.street')}
              value={`${parcel.street}, ${parcel.building}${parcel.apartment ? `/${parcel.apartment}` : ''}`}
            />
            {parcel.additional_info && <Row label={t('parcels.detail.address.info')} value={parcel.additional_info} />}
          </Section>

          <Section title={t('parcels.detail.sections.recipient')}>
            <Row
              label={t('parcels.detail.recipient.fullName')}
              value={`${parcel.last_name} ${parcel.first_name} ${parcel.middle_name}`}
            />
            <Row label={t('parcels.detail.recipient.phone')} value={parcel.phone_number} />
            <Row label={t('parcels.detail.recipient.passport')} value={parcel.passport_series} />
            <Row label={t('parcels.detail.recipient.pinfl')} value={<span className="font-mono">{parcel.pinfl}</span>} />
            {parcel.store_name && <Row label={t('parcels.detail.recipient.store')} value={parcel.store_name} />}
          </Section>
        </div>

        {/* Owner rule (#56, 2026-05-31): surface the linked Cargo ID + its
            owner so admins can see who the parcel belongs to without
            opening a second screen. */}
        <Section title={t('parcels.detail.sections.cargoId')}>
          {parcel.cargo_id_detail ? (
            <>
              <Row
                label={t('parcels.detail.cargoId.code')}
                value={
                  <span className="font-mono">{parcel.cargo_id_detail.code ?? '—'}</span>
                }
              />
              <Row
                label={t('parcels.detail.cargoId.fullName')}
                value={parcel.cargo_id_detail.full_name}
              />
              <Row
                label={t('parcels.detail.cargoId.phone')}
                value={parcel.cargo_id_detail.phone_number}
              />
            </>
          ) : (
            <p className="text-sm italic text-ink-muted">
              {t('parcels.detail.cargoId.empty')}
            </p>
          )}
        </Section>

        <Section title={t('parcels.detail.sections.products', { count: products.length })}>
          {products.length === 0 ? (
            <p className="text-sm text-ink-muted">{t('parcels.detail.products.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-ink-muted">
                  <tr>
                    <th className="px-3 py-2">{t('parcels.detail.products.name')}</th>
                    <th className="px-3 py-2 text-right">{t('parcels.detail.products.quantity')}</th>
                    <th className="px-3 py-2 text-right">{t('parcels.detail.products.unitPrice')}</th>
                    <th className="px-3 py-2 text-right">{t('parcels.detail.products.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-ink-soft">{p.full_name}</td>
                      <td className="px-3 py-2 text-right">{p.quantity}</td>
                      <td className="px-3 py-2 text-right">{formatUsd(p.price_per_item)}</td>
                      <td className="px-3 py-2 text-right font-medium">
                        {formatUsd(p.quantity * p.price_per_item)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-sm">
            <span className="text-ink-muted">{t('parcels.detail.products.grandTotal')}</span>
            <span className="text-base font-semibold text-ink">{formatUsd(total)}</span>
          </div>
        </Section>

        <Section title={t('parcels.detail.charges.title')}>
          {chargesQuery.isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : chargesQuery.error ? (
            <ErrorState
              message={extractErrorMessage(
                chargesQuery.error,
                t('cargoIds.detail.charges.loadError'),
              )}
              onRetry={() => void chargesQuery.refetch()}
            />
          ) : (chargesQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-ink-muted">{t('parcels.detail.charges.empty')}</p>
          ) : (
            <FeaturesChargesTable charges={chargesQuery.data ?? []} />
          )}
        </Section>
      </div>

      <FeaturesParcelsApproveDialog parcel={approveTarget} onClose={() => setApproveTarget(null)} />
      <FeaturesParcelsRejectDialog parcel={rejectTarget} onClose={() => setRejectTarget(null)} />
      <FeaturesParcelsDeleteDialog
        parcel={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => navigate('/parcels')}
      />
    </>
  );
};
